import { useState, type FormEvent } from "react";
import {
  priceToCents,
  type OrderDetail,
  type ReceiptInput,
} from "cadendar-shared/ordering/receipts";

export default function ReceiptForm({
  order,
  disabled,
  isSaving,
  onSubmit,
}: {
  order: OrderDetail;
  disabled: boolean;
  isSaving: boolean;
  onSubmit: (lines: ReceiptInput["lines"]) => void;
}) {
  const [selectedIds, setSelectedIds] = useState(() => new Set<string>());
  const [error, setError] = useState<string | null>(null);
  const remainingLines = order.lines.filter(
    (line) => line.receivedQuantity < line.orderedQuantity,
  );
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (disabled || isSaving) return;
    const data = new FormData(event.currentTarget);
    const lines: ReceiptInput["lines"] = [];
    for (const line of remainingLines) {
      if (!selectedIds.has(line.id)) continue;
      const receivedQuantity = Number(data.get(`quantity-${line.id}`));
      const paidUnitPriceCents = priceToCents(data.get(`price-${line.id}`));
      if (
        !Number.isInteger(receivedQuantity) ||
        receivedQuantity <= 0 ||
        receivedQuantity > line.orderedQuantity - line.receivedQuantity ||
        paidUnitPriceCents === null
      ) {
        setError("Vérifiez les quantités et les prix des articles cochés.");
        return;
      }
      lines.push({
        orderLineId: line.id,
        receivedQuantity,
        paidUnitPriceCents,
      });
    }
    if (!lines.length) {
      setError("Cochez au moins un article reçu.");
      return;
    }
    setError(null);
    onSubmit(lines);
  };
  return (
    <form
      aria-label="Enregistrer une réception"
      onSubmit={submit}
      className="space-y-4"
    >
      <p className="text-sm text-slate-600">
        Cochez les articles reçus. Les quantités restantes et les prix sont
        préremplis et modifiables.
      </p>
      <fieldset
        disabled={disabled || isSaving}
        className="space-y-4 disabled:opacity-60"
      >
        {remainingLines.map((line) => {
          const selected = selectedIds.has(line.id);
          const remaining = line.orderedQuantity - line.receivedQuantity;
          return (
            <div
              key={line.id}
              className={`space-y-3 rounded-xl border p-4 ${selected ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-white"}`}
            >
              <label className="flex min-h-11 items-start gap-3">
                <input
                  type="checkbox"
                  checked={selected}
                  className="mt-1 size-6 shrink-0"
                  aria-label={`Article reçu : ${line.commercialName}`}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setSelectedIds((current) => {
                      const ids = new Set(current);
                      if (checked) ids.add(line.id);
                      else ids.delete(line.id);
                      return ids;
                    });
                    setError(null);
                  }}
                />
                <span className="min-w-0">
                  <span className="block font-semibold">
                    {[line.brand, line.commercialName]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                  <span className="block break-words text-sm text-slate-600">
                    Réf. {line.supplierReference}
                  </span>
                  <span className="block text-sm text-slate-600">
                    Reste à recevoir : {remaining}
                  </span>
                </span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm">
                  Quantité reçue
                  <input
                    type="number"
                    inputMode="numeric"
                    name={`quantity-${line.id}`}
                    required
                    min="1"
                    max={remaining}
                    step="1"
                    disabled={!selected}
                    defaultValue={remaining}
                    aria-label={`Quantité reçue pour ${line.commercialName}`}
                    className="mt-1 min-h-12 w-full rounded-lg border border-slate-300 bg-white p-2 text-base disabled:bg-slate-100"
                  />
                </label>
                <label className="text-sm">
                  Prix unitaire (€)
                  <input
                    inputMode="decimal"
                    name={`price-${line.id}`}
                    required
                    disabled={!selected}
                    defaultValue={(line.catalogUnitPriceCents / 100).toFixed(2)}
                    aria-label={`Prix payé pour ${line.commercialName}`}
                    className="mt-1 min-h-12 w-full rounded-lg border border-slate-300 bg-white p-2 text-base disabled:bg-slate-100"
                  />
                </label>
              </div>
            </div>
          );
        })}
        <button
          type="submit"
          disabled={!selectedIds.size}
          className="min-h-12 w-full rounded-lg bg-blue-700 px-4 py-3 font-semibold text-white disabled:opacity-50"
        >
          {isSaving
            ? "Enregistrement…"
            : `Enregistrer la réception (${selectedIds.size})`}
        </button>
      </fieldset>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
    </form>
  );
}
